import { queryOptions } from "@tanstack/react-query";

import type {
  AdminInquiryListQuery,
  InquiryEventType,
  InquiryKind,
  InquiryStatus,
} from "@kansai-ai-hub/schema";

import { apiClient } from "@/lib/api-client";
import { csvToArr } from "@/lib/csv";
import { fetchBlobWithAuth } from "@/lib/fetch-blob";

export type AdminInquiryListItem = {
  id: string;
  kind: InquiryKind;
  status: InquiryStatus;
  name: string;
  organization: string | null;
  email: string;
  bodyExcerpt: string;
  submittedAt: string;
  createdAt: string;
  /** 添付資料の件数。一覧で「詳細を開くべき行」を判別するために使う。 */
  attachmentCount: number;
  /** 申込者がログインパスワードを設定済みか。ハッシュそのものは API から返らない。 */
  hasPassword: boolean;
  /** 担当者名。未割当なら null（ID は一覧では使わないので返らない）。 */
  assigneeName: string | null;
  /**
   * 推薦者（団体）。一覧・詳細のヘッダでバッジにする。
   * 利用申込以外の種別・「なし」・未入力はサーバー側で null に潰される。
   */
  referrer: string | null;
  /** お問い合わせ種別（LP 由来）。一覧の種別別列に出す。持たない種別・未入力は null。 */
  inquiryType: string | null;
  /** セミナー名（LP セミナー申込）。一覧の種別別列に出す。持たない種別・未入力は null。 */
  seminarTitle: string | null;
};

export type AdminInquiryAttachment = {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
};

export type AdminInquiryDetail = AdminInquiryListItem & {
  body: string;
  payload: Record<string, string | string[]>;
  source: string;
  note: string | null;
  attachments: AdminInquiryAttachment[];
  /** 担当者の User ID。担当セレクタで「自分が担当か」を判定するのに使う。 */
  assigneeId: string | null;
  /** この申込から発行済みのアカウント ID（未発行なら null）。 */
  createdUserId: string | null;
  /**
   * 発行済みアカウントの表示用情報。リロードや再訪のあとでも「誰が出来たか」と
   * 組織詳細への導線を出せるよう、id だけでなくサーバーから受け取る。
   */
  createdUser: AdminInquiryCreatedAccount | null;
};

export type AdminInquiryListResponse = {
  items: AdminInquiryListItem[];
  meta: PaginationMeta;
};

/**
 * 申込から発行したアカウント（`POST .../account` の 201 レスポンス）。
 *
 * サーバーは `AuthUserDto` をそのまま返すが、画面で使うのは「誰が出来たか」の表示と
 * 組織詳細への導線だけなので、必要な項目に絞って受ける。全項目を写すと、
 * ログイン中ユーザーの型と紛らわしくなるうえ、使わない項目の変更で無駄に壊れる。
 */
export type AdminInquiryCreatedAccount = {
  id: string;
  email: string;
  name: string;
  organizationId: string;
  organizationName: string;
};

/** 事務局の受信通知先。宛先管理画面でのみ使う（申込ごとではなく全体で 1 つのリスト）。 */
export type AdminInquiryNotificationEmail = {
  id: string;
  email: string;
  createdAt: string;
};

/** AI 返信草案 1 件。再生成しても前の版は消えないので、一覧は複数件になりうる。 */
export type AdminInquiryReplyDraft = {
  id: string;
  body: string;
  model: string;
  promptVersion: string;
  createdAt: string;
};

export type PaginationMeta = {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

/** 対応担当の絞り込みに出す候補（事務局アカウント）。 */
export type AdminInquiryAssignee = {
  id: string;
  name: string;
};

/** タブ / サイドバーのバッジ用。数えるのは status=NEW のみ（0 件の kind もキーが返る）。 */
export type AdminInquirySummary = {
  total: number;
  byKind: Record<InquiryKind, number>;
};

/** 対応履歴 1 件。誰が・いつ・何をしたかを表示するためだけに使う（append-only）。 */
export type AdminInquiryEvent = {
  id: string;
  type: InquiryEventType;
  fromStatus: InquiryStatus | null;
  toStatus: InquiryStatus | null;
  comment: string | null;
  /** 操作した事務局アカウント名。削除済みユーザーなら null。 */
  actorName: string | null;
  createdAt: string;
};

// 種別・対応状況は packages/schema の zod enum を唯一の真実とし、ここでは再エクスポートに留める。
// 独自に union を書き直すと、サーバー側で値が増えたときに型で気づけなくなる。
export type { InquiryEventType, InquiryKind, InquiryStatus };

export const inquiryKeys = {
  all: ["inquiries"] as const,
  lists: () => [...inquiryKeys.all, "list"] as const,
  list: (filters: AdminInquiryListQuery) => [...inquiryKeys.lists(), filters] as const,
  detail: (id: string) => [...inquiryKeys.all, "detail", id] as const,
  /*
   * **`detail(id)` の配下に置かない。** invalidate は前方一致なので、
   * 状態変更・担当変更・メモ保存のたびにロゴの Blob まで取り直すことになる
   * （`staleTime: Infinity` で「画像は差し替わらない」と宣言しているのに）。
   */
  logo: (id: string) => [...inquiryKeys.all, "logo", id] as const,
  events: (id: string) => [...inquiryKeys.detail(id), "events"] as const,
  replyDrafts: (id: string) => [...inquiryKeys.detail(id), "reply-drafts"] as const,
  summary: () => [...inquiryKeys.all, "summary"] as const,
  // 絞り込みの選択肢であって特定の問い合わせに紐づかないので、list の下には置かない
  // （一覧を invalidate するたびに事務局アカウントまで取り直すことになる）。
  assignees: () => [...inquiryKeys.all, "assignees"] as const,
  // 通知先は申込に紐づかない設定なので detail の下には置かない。
  notificationEmails: () => [...inquiryKeys.all, "notification-emails"] as const,
} as const;

export function adminInquiryListQueryOptions(filters: AdminInquiryListQuery) {
  const params = new URLSearchParams();
  if (filters.kind) params.set("kind", filters.kind);
  // 対応状況は**複数指定（statuses）だけを使う**。これを送り忘れると、絞り込みが
  // 「取得済みの 100 件に対する見た目のフィルタ」になり、件数表示も truncated 判定も
  // 絞り込み前の値のままになる（サーバーに条件が届いていないため）。
  if (filters.statuses) params.set("statuses", filters.statuses);
  if (filters.q) params.set("q", filters.q);
  // 対応担当も**サーバーに送る**（statuses と同じ理由。画面側で弾くと件数表示と
  // 取得上限の判定が絞り込み前の値のままになる）。
  if (filters.assignee) params.set("assignee", filters.assignee);
  params.set("page", String(filters.page));
  params.set("pageSize", String(filters.pageSize));

  return queryOptions({
    queryKey: inquiryKeys.list(filters),
    queryFn: (): Promise<AdminInquiryListResponse> =>
      apiClient.get<AdminInquiryListResponse>(`/api/v1/admin/inquiries?${params.toString()}`),
    staleTime: 30_000,
    // 注: useSuspenseQuery では placeholderData が無効化されるため、ここでは指定しない
    // （指定しても効かず「前の結果を保持している」という誤解を生む）。
  });
}

/**
 * タブ / サイドバーの新着件数バッジ。
 *
 * 常時ポーリングはしない（事務局は画面を開きっぱなしにするので、無駄なリクエストが積み上がる）。
 * 代わりに 30 秒の staleTime + ウィンドウフォーカス時の再取得で「戻ってきたら最新」にする。
 * ステータス変更の直後は各 mutation が `inquiryKeys.summary()` を invalidate するので、
 * この間隔に関係なく即座に減る（02_設計方針.md §3.2）。
 */
export function adminInquirySummaryQueryOptions() {
  return queryOptions({
    queryKey: inquiryKeys.summary(),
    queryFn: (): Promise<AdminInquirySummary> =>
      apiClient.get<AdminInquirySummary>("/api/v1/admin/inquiries/summary"),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * 対応担当の絞り込みに出す候補（事務局アカウント）。
 *
 * 事務局アカウントは滅多に増減しないので、一覧より長く寝かせる。
 * `useSuspenseQuery` にはしない: 候補が取れなくても一覧そのものは読めるべきで、
 * 選択肢のためだけに一覧全体を待たせない。
 */
export function adminInquiryAssigneesQueryOptions() {
  return queryOptions({
    queryKey: inquiryKeys.assignees(),
    queryFn: (): Promise<AdminInquiryAssignee[]> =>
      apiClient.get<AdminInquiryAssignee[]>("/api/v1/admin/inquiries/assignees"),
    staleTime: 5 * 60_000,
  });
}

/** 対応履歴（新しい順）。詳細画面のタイムラインでのみ使う。 */
export function adminInquiryEventsQueryOptions(id: string) {
  return queryOptions({
    queryKey: inquiryKeys.events(id),
    queryFn: (): Promise<AdminInquiryEvent[]> =>
      apiClient.get<AdminInquiryEvent[]>(
        `/api/v1/admin/inquiries/${encodeURIComponent(id)}/events`,
      ),
    staleTime: 30_000,
  });
}

/**
 * 保存済みの AI 返信草案（新しい順）。
 *
 * `useSuspenseQuery` にしないのは、草案が取れなくても問い合わせ本文は読めるべきだから
 * （対応履歴と同じ理由）。生成のたびに 1 件増えるだけなので、履歴として全件受け取り、
 * 画面では先頭（＝最新）を初期表示に使う。
 */
export function adminInquiryReplyDraftsQueryOptions(id: string) {
  return queryOptions({
    queryKey: inquiryKeys.replyDrafts(id),
    queryFn: (): Promise<AdminInquiryReplyDraft[]> =>
      apiClient.get<AdminInquiryReplyDraft[]>(
        `/api/v1/admin/inquiries/${encodeURIComponent(id)}/reply-drafts`,
      ),
    staleTime: 30_000,
  });
}

/** お問い合わせ受信時のメール通知先（事務局のメーリングリスト）。 */
export function adminInquiryNotificationEmailsQueryOptions() {
  return queryOptions({
    /*
     * **フォーカスで取り直す。** この画面は「他の担当者が保存したら警告を出す」作りだが、
     * 再取得の契機が無いと画面を開いたままでは永久に検知できず、警告そのものが死ぬ
     * （グローバル既定は `refetchOnWindowFocus: false`）。
     */
    refetchOnWindowFocus: true,
    queryKey: inquiryKeys.notificationEmails(),
    queryFn: (): Promise<AdminInquiryNotificationEmail[]> =>
      apiClient.get<AdminInquiryNotificationEmail[]>("/api/v1/admin/inquiry-notification-emails"),
    staleTime: 30_000,
  });
}

/**
 * 申込ロゴを Blob として取得する。
 *
 * `<img src="…/logo">` で直接読ませない理由は 2 つある:
 *
 * 1. API はグローバルに `Cross-Origin-Resource-Policy: same-site` を返す。dev は
 *    web(*.azurestaticapps.net) と api(*.azurecontainerapps.io) で eTLD+1 が違うため、
 *    `<img>`（no-cors 読み込み）はブラウザにブロックされる。CORP は CORS モードの
 *    fetch には適用されないので、こちらなら dev でも表示できる。
 * 2. そのために CORP を緩めると、ADMIN 限定のはずの画像を第三者サイトが
 *    `<img>` で埋め込めてしまう（dev の認証 Cookie は sameSite:"none"）。
 *    fetch にすればヘッダを緩めずに済む。
 *
 * **ただし fetch にしただけでは動かない。** Blob Storage 実装は既定で SAS URL への
 * 302 リダイレクトを返し、fetch がクロスオリジンのリダイレクトを追うと以降のリクエストが
 * opaque origin (`"null"`) になって Storage の CORS に弾かれる（本番で実際に起きたバグ。
 * `components/feedback/file-preview.tsx` 参照）。そのため配信側で `preferProxy: true` を
 * 指定し、API 自身が本体をストリームするようにしてある（`admin-inquiries.controller.ts`）。
 * **どちらか片方だけでは表示できない**ので、両方を対で維持すること。
 */
export function adminInquiryLogoQueryOptions(id: string) {
  return queryOptions({
    queryKey: inquiryKeys.logo(id),
    queryFn: (): Promise<Blob> =>
      fetchBlobWithAuth(`/api/v1/admin/inquiries/${encodeURIComponent(id)}/logo`),
    // 画像は差し替わらないので取り直さない。
    staleTime: Infinity,
    retry: false,
  });
}

export function adminInquiryDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: inquiryKeys.detail(id),
    queryFn: (): Promise<AdminInquiryDetail> =>
      // id は URL パスに埋まるので必ずエンコードする（cuid なら実質不要だが、
      // 細工された値でリクエスト先が別パスに向くのを構造的に防ぐ）。
      apiClient.get<AdminInquiryDetail>(`/api/v1/admin/inquiries/${encodeURIComponent(id)}`),
    staleTime: 30_000,
  });
}

// =============================================================================
// 一覧の絞り込みビュー
// =============================================================================

/**
 * 一覧が 1 度に取りに行く上限（API の `pageSize` の最大値）。
 *
 * 一覧は**状態ごとにグループ分けして畳めるようにする**ため、ページ送りではなく
 * 「絞り込んだ結果をまとめて取り、画面側で束ねる」形にしている。
 * ページ送りだと 1 つのグループがページをまたいで分断され、畳む単位と一致しない。
 * 上限に達した場合は一覧に注意書きを出し、検索や状態の絞り込みで狭めてもらう。
 */
export const INQUIRY_LIST_FETCH_SIZE = 100;

/**
 * 一覧が受け取る検索条件。ルートの `validateSearch` と同じ形。
 *
 * `page` / `pageSize` は URL に持たない（一覧はグループ表示でページ送りをしないため）。
 * API のクエリ型は既定値を持つので必須になっており、そのままでは合わない。
 */
export type InquiryListSearch = Omit<AdminInquiryListQuery, "page" | "pageSize"> & {
  page?: number;
  pageSize?: number;
};

/**
 * URL の `statuses` を、その種別で実際に使える状態だけに絞る。
 *
 * **取得条件と表示する見出しは必ずこの 1 つの結果から作る。** 別々に計算すると、
 * 「サーバーには SPAM で絞り込みを投げているのに、画面は申請の全状態を見出しに出す」
 * のような食い違いが起き、全グループが「該当なし」なのに件数だけ出る状態になる。
 *
 * 空配列 = 絞り込み無し（その種別の全状態）。
 */
export function resolveInquiryStatuses(
  search: InquiryListSearch,
  kindStatuses: readonly InquiryStatus[],
): readonly InquiryStatus[] {
  /*
   * **並びは URL ではなく `kindStatuses` 側を正とする。** URL の順にすると
   * 「回答済み → 新着」の順でチェックしただけでグループの並びが業務の進み方と逆になり、
   * その並びが共有 URL の受け手にも伝播する。Set で引くので重複指定
   * （`statuses=NEW,NEW`）も 1 つに畳まれる（畳まないと同じ tbody が 2 つ描かれる）。
   */
  const requested = new Set(csvToArr(search.statuses));
  return kindStatuses.filter((s) => requested.has(s));
}

/**
 * URL の検索条件から一覧クエリを組み立てる。
 *
 * `statuses`（カンマ区切り）は API がそのまま解釈するので、**1 リクエストで済む**。
 * 状態ごとに 1 本ずつ投げる案は採らない: 1 操作あたりのリクエスト数が状態数だけ増え、
 * どれか 1 本が失敗したときに「一部の状態だけ古い一覧」という直しづらい状態になる。
 */
export function resolveInquiryListQuery(
  search: InquiryListSearch,
  kind: InquiryKind,
  kindStatuses: readonly InquiryStatus[],
): AdminInquiryListQuery {
  const statuses = resolveInquiryStatuses(search, kindStatuses);
  return {
    kind,
    ...(search.q ? { q: search.q } : {}),
    ...(statuses.length > 0 ? { statuses: statuses.join(",") } : {}),
    /*
     * 対応担当は**種別に依らない**ので、statuses のように「その kind で使える値」へ
     * 丸める処理は要らない。未知の ID はサーバー側が該当 0 件として扱う。
     */
    ...(search.assignee ? { assignee: search.assignee } : {}),
    page: 1,
    pageSize: INQUIRY_LIST_FETCH_SIZE,
  };
}

/** 状態ごとに束ねた一覧。畳める見出し 1 つ分に対応する。 */
export type InquiryStatusGroup = {
  status: InquiryStatus;
  items: AdminInquiryListItem[];
};

/**
 * 取得した行を状態ごとに束ねる。
 *
 * `statuses` に**表示したい状態を必ず渡す**（空なら kind の全状態）。0 件のグループも
 * 見出しごと出したいので、行の有無ではなくこの並びを正とする。
 * 「回答済みが 0 件」と「回答済みの見出しが無い」は事務局にとって意味が違う。
 */
export function groupInquiriesByStatus(
  items: AdminInquiryListItem[],
  statuses: readonly InquiryStatus[],
): InquiryStatusGroup[] {
  const byStatus = new Map<InquiryStatus, AdminInquiryListItem[]>(
    statuses.map((status) => [status, []]),
  );
  /*
   * **想定外の状態を握りつぶさない。** 種別の既定リストに無い状態の行
   * （申請なのに `SPAM` の過去データなど）を捨てると、「全 5 件」と出ているのに
   * 4 行しか描画されず、しかもその行にはどこからも辿り着けなくなる。
   * 遷移表は SPAM からの差し戻し路を残しているので、画面にも出す必要がある。
   */
  const extra: InquiryStatus[] = [];
  for (const item of items) {
    const bucket = byStatus.get(item.status);
    if (bucket) {
      bucket.push(item);
      continue;
    }
    byStatus.set(item.status, [item]);
    extra.push(item.status);
  }
  return [...statuses, ...extra].map((status) => ({
    status,
    items: byStatus.get(status) ?? [],
  }));
}
